import type { InvalidInvitationReason } from '@/lib/preboarding-public';
import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';

interface PreboardingErrorProps {
  reason?: InvalidInvitationReason;
}

const COPY: Record<
  InvalidInvitationReason | 'DEFAULT',
  { title: string; message: string; icon: typeof AlertCircle }
> = {
  EXPIRED: {
    title: 'This link has expired',
    message: 'Pre-boarding links are valid for a few days. Please ask HR to send you a new one.',
    icon: Clock,
  },
  COMPLETED: {
    title: "You're all done",
    message: 'This form has already been submitted. There is nothing more to do here.',
    icon: CheckCircle2,
  },
  REVOKED: {
    title: 'This link is no longer active',
    message: 'A newer link was issued for this form. Please use the most recent email from HR.',
    icon: AlertCircle,
  },
  NOT_FOUND: {
    title: 'Link not found',
    message: 'This link is invalid or incomplete. Please open it directly from your email.',
    icon: AlertCircle,
  },
  DEFAULT: {
    title: 'Something went wrong',
    message: 'We could not open this pre-boarding form. Please try again or contact HR.',
    icon: AlertCircle,
  },
};

export function PreboardingError({ reason }: PreboardingErrorProps) {
  const { title, message, icon: Icon } = COPY[reason ?? 'DEFAULT'] ?? COPY.DEFAULT;

  return (
    <div className="mx-auto flex max-w-[440px] flex-col items-center gap-4 px-4 py-24 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
